import { Injectable } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { fetchAndActivate, getAll, getRemoteConfig } from '@angular/fire/remote-config';
import { from, Observable } from 'rxjs';
import { RemoteConfig } from '@qvs/core';
import { RemoteConfigMapper } from '../mapper';
import { Store } from '@ngrx/store';
import { loadRemoteConfig } from '../store';
import { firebaseConfig } from '@environments/firebaseConfig';
import { FirebaseInstallationsService } from './firebaseInstallations.service';

@Injectable({
  providedIn: 'root'
})
export class RemoteConfigService {
  private installationRecoveryAttemptedInSession = false;

  constructor(
    private readonly firebaseApp: FirebaseApp,
    private readonly store: Store,
    private readonly firebaseInstallationsService: FirebaseInstallationsService
  ) {}

  public init() {
    getRemoteConfig(this.firebaseApp).settings.minimumFetchIntervalMillis =
      firebaseConfig.remoteConfig.minimumFetchIntervalMillis;
    this.store.dispatch(loadRemoteConfig());
  }

  public getAllValues = (): Observable<RemoteConfig> => from(this.fetchWithInstallationRecovery());

  private isInstallationNotFound(error: unknown): boolean {
    const err = error as { code?: string };
    return err?.code === 'installations/installation-not-found';
  }

  private async fetchWithInstallationRecovery(): Promise<RemoteConfig> {
    try {
      return await this.fetchAndMapRemoteConfig();
    } catch (error) {
      if (!this.isInstallationNotFound(error) || this.installationRecoveryAttemptedInSession) {
        throw error;
      }

      this.installationRecoveryAttemptedInSession = true;
      await this.firebaseInstallationsService.recreateInstallationId();
      return this.fetchAndMapRemoteConfig();
    }
  }

  private async fetchAndMapRemoteConfig(): Promise<RemoteConfig> {
    await fetchAndActivate(getRemoteConfig(this.firebaseApp));
    return RemoteConfigMapper.fromFirebaseRemoteConfig(getAll(getRemoteConfig(this.firebaseApp)));
  }
}
